/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useEffect, useState } from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Modal } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import TemplateFolderSelect from './templateFolderSelect';

const DialogBox = styled.form`
  width: min(440px, calc(100vw - 32px));
  height: ${({ $height }) => $height}px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
  background: #000;
  border: 0;
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px #3a3d3f;
  box-sizing: border-box;
  color: #fff;
  overflow: ${({ $allowOverflow }) => ($allowOverflow ? 'visible' : 'hidden')};
  font-family: ${({ theme }) => theme.typography.family.primary};
`;

const DialogTitle = styled.h2`
  margin: 0;
  width: 100%;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 19px;
  letter-spacing: 0;
`;

const DialogText = styled.p`
  margin: 0;
  width: 100%;
  color: #fff;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
`;

const DialogField = styled.div`
  height: 58px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DialogLabel = styled.label`
  height: 16px;
  color: #fff;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
`;

const DialogTextInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 12px;
  box-sizing: border-box;
  background: #000;
  border: 1px solid #393d3f;
  border-radius: 6px;
  color: #fff;
  font-family: ${({ theme }) => theme.typography.family.primary};
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
  outline: none;

  &&:focus {
    border-color: #393d3f;
    outline: 2px solid #76a0f0 !important;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const DialogFooter = styled.div`
  height: 32px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const DialogButton = styled.button`
  width: ${({ $width }) => $width}px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: ${({ $variant }) => {
    if ($variant === 'danger') {
      return '#d24747';
    }
    if ($variant === 'primary') {
      return '#1c62eb';
    }
    return '#3c434a';
  }};
  color: #fff;
  cursor: pointer;
  font-family: ${({ theme }) => theme.typography.family.primary};
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
  opacity: ${({ disabled, $variant }) =>
    disabled && $variant !== 'secondary' ? 0.5 : 1};

  &:disabled {
    cursor: not-allowed;
  }

  &:focus {
    outline: 2px solid #76a0f0;
    outline-offset: 2px;
  }
`;

export function TemplateTitleDialog({
  isOpen,
  isSaving,
  template,
  onClose,
  onSave,
}) {
  const [title, setTitle] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTitle(template?.title || '');
    }
  }, [isOpen, template]);

  const canSave =
    title.trim().length > 0 &&
    title.trim() !== (template?.title || '') &&
    !isSaving;

  const handleSave = () => {
    if (canSave) {
      onSave(title.trim());
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaHideApp={false}
      contentLabel="Edit Template Title"
    >
      <DialogBox
        $height={189}
        onSubmit={(event) => {
          event.preventDefault();
          handleSave();
        }}
      >
        <DialogTitle>Edit Template Title</DialogTitle>
        <DialogField>
          <DialogLabel htmlFor="template-title-input">
            Template title
          </DialogLabel>
          <DialogTextInput
            id="template-title-input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={isSaving}
            autoComplete="off"
            autoFocus
          />
        </DialogField>
        <DialogFooter>
          <DialogButton type="button" $width={83} onClick={onClose}>
            Cancel
          </DialogButton>
          <DialogButton
            type="submit"
            $width={70}
            $variant="primary"
            disabled={!canSave}
          >
            Save
          </DialogButton>
        </DialogFooter>
      </DialogBox>
    </Modal>
  );
}

TemplateTitleDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool,
  template: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export function TemplateMoveDialog({
  folders,
  isLoading,
  loadError,
  isOpen,
  isSaving,
  template,
  onClose,
  onSave,
}) {
  const [folderId, setFolderId] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFolderId(template?.folderId || '');
    }
  }, [isOpen, template]);

  const destinationExists = folders.some((folder) => folder.id === folderId);
  const canSave =
    destinationExists &&
    folderId !== (template?.folderId || '') &&
    !isLoading &&
    !loadError &&
    !isSaving;

  const handleSave = () => {
    if (canSave) {
      onSave(folderId);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaHideApp={false}
      contentLabel="Move to Folder"
      contentStyles={{
        overflow: 'visible',
      }}
    >
      <DialogBox
        $height={189}
        $allowOverflow
        onSubmit={(event) => {
          event.preventDefault();
          handleSave();
        }}
      >
        <DialogTitle>Move to Folder</DialogTitle>
        <DialogField>
          <DialogLabel htmlFor="template-folder-input">
            Destination Folder
          </DialogLabel>
          <TemplateFolderSelect
            inputId="template-folder-input"
            folders={folders}
            value={folderId}
            onChange={setFolderId}
            isLoading={isLoading}
            placeholder={
              isLoading ? 'Loading folders...' : 'Choose an existing folder...'
            }
            disabled={isSaving || isLoading || loadError}
          />
        </DialogField>
        {loadError && (
          <DialogText role="alert">
            Unable to load folders. Close and reopen this dialog to retry.
          </DialogText>
        )}
        <DialogFooter>
          <DialogButton type="button" $width={83} onClick={onClose}>
            Cancel
          </DialogButton>
          <DialogButton
            type="submit"
            $width={70}
            $variant="primary"
            disabled={!canSave}
          >
            Save
          </DialogButton>
        </DialogFooter>
      </DialogBox>
    </Modal>
  );
}

TemplateMoveDialog.propTypes = {
  folders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      path: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  isLoading: PropTypes.bool,
  loadError: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool,
  template: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export function TemplateDeleteDialog({
  isOpen,
  isSaving,
  template,
  onClose,
  onDelete,
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaHideApp={false}
      contentLabel="Delete template"
    >
      <DialogBox $height={166} as="div" role="document">
        <DialogTitle>
          Are you sure you want to delete “{template?.title || 'this template'}”
          template?
        </DialogTitle>
        <DialogText>This cannot be undone.</DialogText>
        <DialogFooter>
          <DialogButton type="button" $width={83} onClick={onClose}>
            Cancel
          </DialogButton>
          <DialogButton
            type="button"
            $width={109}
            $variant="danger"
            disabled={isSaving}
            onClick={onDelete}
          >
            Yes, Delete
          </DialogButton>
        </DialogFooter>
      </DialogBox>
    </Modal>
  );
}

TemplateDeleteDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool,
  template: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
